import {
	useMemo,
	useRef,
	useState,
	useContext,
	createContext,
} from 'react';
import styles from './authentication-dialog.module.css';
import { useOnClickOutside, SignUpProps } from '@shared/auth';
import { useT } from '@shared/i18n';

export const SignUpCognito = (args: SignUpProps) => {
	const [mode, setMode] = useState<Mode>('signup');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [code, setCode] = useState('');
	const contextValue = useMemo(
		() => ({
			mode,
			setMode,
			email,
			setEmail,
			password,
			setPassword,
			code,
			setCode,
		}),
		[mode, email, password, code],
	);

	return (
		<SignUpContext.Provider value={contextValue}>
			{mode === 'signup' ? <SignUp {...args} /> : <Confirm {...args} />}
		</SignUpContext.Provider>
	);
};

export const SignUp = ({
	onClickLogIn,
	onCloseDialog,
}: SignUpProps) => {
	const t = useT();
	const { email, setEmail, password, setPassword, setMode } =
		useContext(SignUpContext);
	const ref = useRef(null);
	useOnClickOutside(ref, () => {
		onCloseDialog();
	});
	const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setMode('confirm');
	};
	return (
		<form ref={ref} onSubmit={onSubmit}>
			<div className={styles['details']}>
				<h3>{t('auth.createAccount')}</h3>
				<h4>{t('auth.startNow')}</h4>
				<div className={styles['input-control']}>
					<label htmlFor="email-input">{t('auth.email')}</label>
					<input
						type="email"
						id="email-input"
						name="email"
						value={email}
						onChange={(e) => {
							setEmail(e.target.value);
						}}
						required
					/>
				</div>
				<div className={styles['input-control']}>
					<label htmlFor="password-input">{t('auth.password')}</label>
					<input
						minLength={8}
						type="password"
						id="password-input"
						name="password"
						value={password}
						onChange={(e) => {
							setPassword(e.target.value);
						}}
						required
					/>
				</div>
			</div>
			<div className={styles['control']}>
				<button>{t('common.signUp')}</button>
				<div>
					<p>{t('auth.haveAccount')}</p>
					<button onClick={onClickLogIn}>{t('common.signIn')}</button>
				</div>
			</div>
		</form>
	);
};

export const Confirm = ({ onCloseDialog, onSuccess }: SignUpProps) => {
	const t = useT();
	const { code, setCode } = useContext(SignUpContext);
	const ref = useRef(null);
	useOnClickOutside(ref, () => {
		onCloseDialog();
	});
	const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		onSuccess();
	};
	return (
		<form ref={ref} onSubmit={onSubmit}>
			<div className={styles['details']}>
				<h3>{t('auth.confirmationTitle')}</h3>
				<h4>{t('auth.startNow')}</h4>
				<div className={styles['input-control']}>
					<label htmlFor="code-input">{t('auth.confirmationCode')}</label>
					<input
						type="number"
						minLength={6}
						maxLength={6}
						id="code-input"
						name="code"
						value={code}
						onChange={(e) => {
							setCode(e.target.value);
						}}
						required
					/>
				</div>
			</div>
			<div className={styles['control']}>
				<button>{t('auth.confirm')}</button>
			</div>
		</form>
	);
};

export const SignUpContext = createContext<SignUpContextProps>({
	mode: 'signup',
	setMode: () => {},
	email: '',
	setEmail: () => {},
	password: '',
	setPassword: () => {},
	code: '',
	setCode: () => {},
});

interface SignUpContextProps {
	mode: Mode;
	setMode: (e: Mode) => void;
	email: string;
	setEmail: (e: string) => void;
	password: string;
	setPassword: (e: string) => void;
	code: string;
	setCode: (e: string) => void;
}

type Mode = 'signup' | 'confirm';
